function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
import React, { useMemo, useState } from 'react';
import { CustomModal, MultiSelectFilterPicker, Page, PageContentHeader, PageEmptyState, PageErrorState, Searchbox, TableList, Typography, useDidUpdate, useOverlayContext } from 'seagull';
import LoadingIndicator from "../../../components/LoadingIndicator";
import { useRestfulWrapper } from "../../../hooks/useRestfulWrapper";
import { useServiceTemplateApi } from "../../../hooks/useServiceTemplateApi";
import { modalSize } from "../../../infra/modals";
import NoDataIcon from "../../assets/NoDataIcon";
import { CreateProposalButton } from "../../../components/CreateProposalButton/CreateProposalButton";
import { serviceTemplateModel } from "../../../models";
import styles from "./PMMapServicesModal.module.scss";
import { handlePromise } from "../../../infra/utils";
import PMMapServiceField from "../PMMapServiceField/PMMapServiceField";
import { getPMDetails } from "../pm.utils";
function getTableConfig(pmName) {
  const tableConfig = [{
    columnDef: {
      width: [100, 500]
    },
    headerItem: {
      value: 'Anchor service'
    }
  }, {
    columnDef: {
      width: 550
    },
    headerItem: {
      value: "".concat(getPMDetails(pmName).name, " ").concat(getPMDetails(pmName).template)
    }
  }];
  return {
    columnDefs: tableConfig.map(config => config.columnDef),
    headerItems: tableConfig.map(config => config.headerItem)
  };
}
var MappedStatus = /*#__PURE__*/function (MappedStatus) {
  MappedStatus["Mapped"] = "mapped";
  MappedStatus["Unmapped"] = "unmapped";
  return MappedStatus;
}(MappedStatus || {});
function filterTemplates(templates, search, mappedStatusFilter, getServiceTemplateIntegratedIds) {
  const trimmedLowercaseSearch = search.trim().toLowerCase();
  return templates.filter(template => {
    const includedBySearch = !trimmedLowercaseSearch || template.name.toLowerCase().includes(trimmedLowercaseSearch);
    const isMapped = getServiceTemplateIntegratedIds(template).length > 0;
    const includedByMappedStatus = mappedStatusFilter.length === 0 || mappedStatusFilter.includes(MappedStatus.Mapped) && isMapped || mappedStatusFilter.includes(MappedStatus.Unmapped) && !isMapped;
    return includedBySearch && includedByMappedStatus;
  });
}
const mappedStatusFilterOptions = [{
  value: MappedStatus.Mapped,
  label: 'Mapped'
}, {
  value: MappedStatus.Unmapped,
  label: 'Unmapped'
}];
function PMMapServiceRow(_ref) {
  let {
    template,
    testid,
    items,
    getServiceTemplateIntegratedIds,
    onMapService
  } = _ref;
  const [integratedIds, setIntegratedIds] = useState(() => getServiceTemplateIntegratedIds(template));
  useDidUpdate(() => setIntegratedIds(getServiceTemplateIntegratedIds(template)), [template]);
  const isDeleted = serviceTemplateModel.isDeleted(template);
  return /*#__PURE__*/React.createElement(TableList.Row, {
    key: template.id,
    size: "fluid",
    className: styles.row,
    items: [{
      value: /*#__PURE__*/React.createElement(Typography, {
        ellipsis: true,
        skin: isDeleted ? 'secondary' : 'standard'
      }, "".concat(template.name).concat(isDeleted ? ' (Archived)' : ''))
    }, {
      value: /*#__PURE__*/React.createElement(PMMapServiceField, {
        testid: "".concat(testid, "-mapping"),
        key: template.id,
        options: items,
        value: integratedIds,
        onChange: async ids => {
          setIntegratedIds(ids);
          onMapService(template, ids);
        }
      })
    }]
  });
}
function PMMapServicesTable(_ref2) {
  let {
    pmName,
    templates,
    items,
    getServiceTemplateIntegratedIds,
    linkServiceTemplate,
    refresh
  } = _ref2;
  const {
    columnDefs,
    headerItems
  } = useMemo(() => getTableConfig(pmName), [pmName]);
  const [search, setSearch] = useState('');
  const [mappedStatusFilter, setMappedStatusFilter] = useState([]);
  const {
    showErrorToast
  } = useOverlayContext();
  const filteredTemplates = useMemo(() => filterTemplates(templates, search, mappedStatusFilter, getServiceTemplateIntegratedIds), [templates, search, mappedStatusFilter, getServiceTemplateIntegratedIds]);
  async function mapService(template, itemIds) {
    const result = await handlePromise(linkServiceTemplate(template.id, itemIds));
    if (!result.success) {
      await refresh();
      showErrorToast("Failed to map service ".concat(template.name), 'map-service-error-toast');
    }
  }
  return /*#__PURE__*/React.createElement(TableList.Provider, {
    columnDefs: columnDefs
  }, /*#__PURE__*/React.createElement(PageContentHeader, null, /*#__PURE__*/React.createElement("div", {
    className: styles.filtersRow
  }, /*#__PURE__*/React.createElement(Searchbox, {
    "data-testid": "services-search",
    value: search,
    onChange: e => setSearch(e.target.value),
    onClear: () => setSearch('')
  }), /*#__PURE__*/React.createElement(MultiSelectFilterPicker, {
    value: mappedStatusFilter,
    options: mappedStatusFilterOptions,
    label: "Mapped status",
    onChange: setMappedStatusFilter
  })), /*#__PURE__*/React.createElement(TableList.Header, {
    items: headerItems
  })), /*#__PURE__*/React.createElement("div", null, filteredTemplates.map((template, idx) => /*#__PURE__*/React.createElement(PMMapServiceRow, {
    key: template.id,
    testid: "service-row-".concat(idx),
    items: items,
    template: template,
    getServiceTemplateIntegratedIds: getServiceTemplateIntegratedIds,
    onMapService: mapService
  }))));
}
export default function PMMapServicesModal(props) {
  const {
    pmName,
    dismissModal,
    getItems
  } = props;
  const serviceTemplateApi = useServiceTemplateApi();
  const {
    renderRestfulState
  } = useRestfulWrapper({
    fetch
  });
  async function fetch() {
    const [templates, items] = await Promise.all([serviceTemplateApi.get({
      includeDeleted: true,
      includeAdhocCharges: true
    }), getItems()]);
    return {
      templates: templates.raw,
      items
    };
  }
  return /*#__PURE__*/React.createElement(CustomModal, _extends({
    "data-testid": "pm-map-services-modal"
  }, modalSize.large, {
    onClose: dismissModal
  }), /*#__PURE__*/React.createElement(Page, {
    height: "100%",
    header: "Map Services to ".concat(getPMDetails(pmName).name),
    skin: "secondary"
  }, renderRestfulState({
    renderChild: _ref3 => {
      let {
        data,
        refresh
      } = _ref3;
      return /*#__PURE__*/React.createElement("div", {
        "data-testid": "pm-map-services-content"
      }, data.templates.length > 0 ? /*#__PURE__*/React.createElement(PMMapServicesTable, _extends({}, props, data, {
        refresh: refresh
      })) : /*#__PURE__*/React.createElement(PageEmptyState, {
        "data-testid": "pm-map-services-no-services",
        illustration: /*#__PURE__*/React.createElement(NoDataIcon, null),
        message: "In order to map with ".concat(getPMDetails(pmName).name, ", start by creating an agreement"),
        actions: /*#__PURE__*/React.createElement(CreateProposalButton, {
          text: "Create a new agreement"
        })
      }));
    },
    renderLoader: () => /*#__PURE__*/React.createElement(LoadingIndicator, null),
    renderError: _ref4 => {
      let {
        refresh
      } = _ref4;
      return /*#__PURE__*/React.createElement(PageErrorState, {
        "data-testid": "pm-map-services-fetch-error",
        message: "Failed to retrieve the relevant data please try again later",
        onRefresh: refresh,
        skin: "secondary"
      });
    }
  })));
}